import React, { PureComponent } from 'react';
import styled from 'styled-components';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import { Spinner } from '@zocdoc-frontend-common/components-spinner';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import { Row } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    blue70,
    blue100,
    red80,
    slate30,
    slate40,
} from '@zocdoc-mezzanine/foundations-colors';

export const EMAIL_TAKEN_MESSAGE = 'That email is already in use.';

/**
 * @param {boolean} confirmEmailError
 * @param {boolean} emailError
 * @param {function(): void} handleClickLogin
 * @param {boolean} inlineEmailLoading
 * @param {boolean} inlineEmailTaken
 * @param {function(): void} onBlur
 * @param {function(): void} onChange
 * @param {function(): void} onFocus
 * @param {boolean} submitted
 * @returns {JSX.Element}
 * @constructor
 */
export default class EmailFields extends PureComponent {
    render() {
        const { onFocus, onBlur, onChange, emailError, confirmEmailError } =
            this.props;

        return (
            <React.Fragment>
                <FormRow>
                    <FormLabel>Enter your email</FormLabel>
                    <StyledRow>
                        <StyledTextBox
                            autoComplete="username"
                            className="exclude-from-playback"
                            data-test="email"
                            data-uem-id="email"
                            inputMode="email"
                            invalid={emailError}
                            name="email"
                            onChange={(e) => {
                                onChange('email', e.target.value);
                            }}
                            onBlur={(e) => {
                                onBlur('email', e.target.value);
                            }}
                            onFocus={() => {
                                onFocus('email');
                            }}
                            type="email"
                        />
                        {this.maybeRenderSpinner()}
                    </StyledRow>

                    {this.maybRenderInvalidEmailMessage()}
                    {this.maybeRenderTakenHint()}
                </FormRow>
                <FormRow>
                    <FormLabel>Confirm your email</FormLabel>
                    <Row>
                        <StyledTextBox
                            autoComplete="username"
                            className="exclude-from-playback"
                            data-test="confirm-email"
                            data-uem-id="confirm-email"
                            inputMode="email"
                            invalid={confirmEmailError}
                            name="confirmEmail"
                            onChange={(e) => {
                                onChange('confirmEmail', e.target.value);
                            }}
                            onBlur={(e) => {
                                onBlur('confirmEmail', e.target.value);
                            }}
                            onFocus={() => {
                                onFocus('confirmEmail');
                            }}
                            type="email"
                        />
                    </Row>
                    {this.maybeRenderInvalidConfirmEmailMessage()}
                </FormRow>
            </React.Fragment>
        );
    }

    maybeRenderSpinner = () => {
        const { inlineEmailLoading } = this.props;
        if (inlineEmailLoading) {
            return (
                <SpinnerWrapper>
                    <Spinner inline size={24} />
                </SpinnerWrapper>
            );
        }
    };

    maybRenderInvalidEmailMessage() {
        const { emailError } = this.props;
        if (emailError) {
            return (
                <Row>
                    <ErrorMessage data-test="email-error">
                        Please type a valid email.
                    </ErrorMessage>
                </Row>
            );
        }
    }
    maybeRenderInvalidConfirmEmailMessage() {
        const { confirmEmailError } = this.props;
        if (confirmEmailError) {
            return (
                <Row>
                    <ErrorMessage data-test="confirm-email-error">
                        The confirm email must match your email address.
                    </ErrorMessage>
                </Row>
            );
        }
    }

    maybeRenderTakenHint() {
        const { inlineEmailTaken, submitted, handleClickLogin } = this.props;

        if (inlineEmailTaken) {
            return (
                <TakenHintContainer
                    submitted={submitted}
                    data-test="email-taken-container"
                >
                    <CTA>
                        <Text data-test="email-taken-text">
                            Looks like you already have an account.
                        </Text>
                        <Link onClick={handleClickLogin}>Log in now</Link>
                    </CTA>
                </TakenHintContainer>
            );
        }
    }
}

const StyledRow = styled(Row)`
    position: relative;
`;

const SpinnerWrapper = styled.div`
    position: absolute;
    height: 24px;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    > div > div {
        :before {
            border: 3px solid ${slate30};
            border-top-color: ${blue100};
        }
    }
`;

const TakenHintContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    border: 1px solid;
    border-color: ${(props) => (props.submitted ? red80 : slate40)};
    margin-top: 8px;
    padding: 10px 0px 10px 16px;
    font: 15px ${FontFamily.SharpSansMedium};
    line-height: 24px;
    background-color: ${(props) => (props.submitted ? '#ffecf1' : 'inherent')};
    ${breakpoint.medium} {
        height: 48px;
    }
`;

const CTA = styled.span`
    align-self: center;
`;
const Text = styled.span`
    padding-right: 4px;
`;
const Link = styled.a`
    display: inline-block;
    border-bottom: 2px dotted;
    color: ${blue70};
    flex-basis: 100px;
    cursor: pointer;
`;

const StyledTextBox = styled(Textbox)`
    ${(p) => (p.invalid ? `border-color: ${red80}` : '')};
`;

const FormLabel = styled.div`
    color: ${blue100};
    font: 15px/24px ${FontFamily.SharpSansSemibold};
`;
